// EXPORT-CORE (docs/design/2026-10-30-client-offboarding-portability.md §2, §3.3, §5): "export any
// time". An owner downloads the Company Archive (`companyos.company-archive/v1`, a zip of open
// formats: Markdown with frontmatter, JSON, JSONL, CSV and the files as uploaded), built read-only
// inside the company's own tenant transaction and passed through a fail-closed secret scan. The
// owner confirms with their password (and a current two-step code when they use two-step sign-in).
// The Leave flow, deletion, key revocation and the standalone package are other packages.
import { z } from "zod";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

export const COMPANY_ARCHIVE_FORMAT = "companyos.company-archive/v1";

/** `POST …/export`: the re-authentication that lets an owner take the whole company out. */
export const CompanyExportRequest = z
  .strictObject({
    password: z
      .string()
      .min(1)
      .max(1024)
      .optional()
      .describe("The owner's password (re-authentication)"),
    totp_code: z
      .string()
      .max(6)
      .regex(/^\d{6}$/)
      .optional()
      .describe("A current two-step code; required when the owner uses two-step sign-in"),
  })
  .meta({ id: "CompanyExportRequest" });
export type CompanyExportRequest = z.infer<typeof CompanyExportRequest>;

export const companyExportRoutes = {
  "companies.export": defineRoute({
    feature: "core",
    id: "companies.export",
    method: "post",
    path: `${base}/export`,
    tag: "companies",
    summary:
      "Download the Company Archive (zip: Markdown, JSON, JSONL, CSV and files). Owners only, re-authenticated",
    description:
      "Built read-only in the company's tenant transaction; every file passes a secret scan and any hit stops the export (`export_blocked`). The archive carries MANIFEST.json (sha256 per file) and audit/redaction-report.json.",
    access: "manager",
    params: CompanyParams,
    body: CompanyExportRequest,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: ["reauth_required", "mfa_code_required", "export_blocked"],
    // A refused download (`export_blocked`, a build error) records `company.export_failed` in a
    // transaction of its own (the `export:refusal` emitter), never as a successful call.
    emits: ["company.export_requested", "company.export_completed"],
  }),
} as const;
